import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { SperrWert } from "../SperrWert";

/**
 * Geteilte, suchbare Dropdown-Combobox im REFINED-Look (gleiche Feld-Styles wie
 * LabeledInput/LabeledSelect → optisch identisch). Controlled: `value` ist der
 * gespeicherte String, `onChange` schreibt zurück. Tippen filtert; ↑/↓/↵/⎋
 * Tastatur; Klick wählt. Freitext erlaubt (`allowFreeText`), damit nicht-
 * gelistete Werte erhalten bleiben. Basis für Nationalität/Rechtsform/Finanzamt.
 */

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  /** Kurzer Zusatz rechts in der Zeile (einzeilig, z. B. das Land zur Nationalität). */
  hint?: string;
  /**
   * Langtext zur Option — erscheint als „ⓘ" am Zeilenende: Hover zeigt ihn als
   * Tooltip, ein Klick klappt ihn unter der Zeile auf, ohne die Option zu wählen.
   * Für Erläuterungen, die zu lang für `hint` sind (Branchen-Spezialisierungen).
   */
  info?: string;
}

const useLocal = makeStyles({
  wrap: { position: "relative" },
  chevron: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    display: "inline-flex",
    color: REFINED.ink3,
  },
  chevPad: { paddingRight: "34px" },
  list: {
    position: "absolute",
    zIndex: 40,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "264px",
    overflowY: "auto",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  opt: {
    display: "flex",
    alignItems: "center",
    // umbruchfähig, damit die aufgeklappte Erläuterung UNTER die Zeile rutscht
    flexWrap: "wrap",
    columnGap: "10px",
    ...shorthands.padding("9px", "12px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    cursor: "pointer",
  },
  optActive: { backgroundColor: REFINED.aub50 },
  optHint: { marginLeft: "auto", fontSize: "0.8rem", color: REFINED.ink3, whiteSpace: "nowrap" },
  /** „ⓘ" am Zeilenende — Hover zeigt den Tooltip, Klick klappt den Text auf. */
  optInfoBtn: {
    marginLeft: "auto",
    flexShrink: 0,
    appearance: "none",
    border: "none",
    background: "none",
    cursor: "help",
    color: REFINED.aub,
    fontWeight: 800,
    fontSize: "0.95rem",
    lineHeight: 1,
    padding: "0 2px",
  },
  optInfoText: {
    flexBasis: "100%",
    fontSize: "0.8rem",
    lineHeight: 1.4,
    color: REFINED.ink2,
    paddingBottom: "6px",
  },
  empty: {
    ...shorthands.padding("10px", "12px"),
    fontSize: "0.86rem",
    color: REFINED.ink3,
    fontFamily: REFINED.fontUI,
  },
});

const norm = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const Chevron: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const ErrIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="8" cy="8" r="6.4" />
    <path d="M8 4.8v3.6" strokeLinecap="round" />
    <circle cx="8" cy="11.1" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

export function SearchableSelect({
  label,
  name,
  value,
  onChange,
  options,
  filter,
  placeholder,
  required,
  invalid,
  errorText,
  leftIcon,
  allowFreeText = true,
  emptyText = "Keine Treffer",
  maxResults = 50,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  /** Statische Optionsliste (clientseitig gefiltert). */
  options?: SelectOption[];
  /** Alternativ: dynamische Filterfunktion (z. B. Finanzamt-Suche). */
  filter?: (query: string) => SelectOption[];
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  errorText?: string;
  leftIcon?: React.ReactNode;
  allowFreeText?: boolean;
  emptyText?: string;
  maxResults?: number;
}) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();
  const [open, setOpen] = React.useState(false);
  const [typed, setTyped] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  /**
   * Markierte Zeile — `-1` heißt AUSDRÜCKLICH „keine".
   *
   * Warum nicht 0: Die Liste klappt schon beim Fokus auf. Stand `active` dabei
   * auf 0, übernahm ein Enter die erste Zeile, die der Anwender nie gesehen
   * hat — im Branche-Feld war das die Zeile „keine ausgewählt", und wer aus
   * Gewohnheit Enter drückte (= speichern), verlor die ganze Klassifikation.
   * Markiert wird deshalb erst durch Tippen oder Pfeiltaste; bis dahin geht
   * Enter ans Formular, wie in jedem anderen Feld auch.
   */
  const [active, setActive] = React.useState(-1);
  /** Option, deren Erläuterung gerade aufgeklappt ist (Klick auf das „ⓘ"). */
  const [infoOffen, setInfoOffen] = React.useState<string | null>(null);
  const blurTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const labelForValue = React.useMemo(() => {
    if (!value) return "";
    const opt = options?.find((o) => o.value === value);
    return opt ? opt.label : value;
  }, [value, options]);

  const results = React.useMemo<SelectOption[]>(() => {
    const q = typed ? query.trim() : "";
    if (filter) return filter(q).slice(0, maxResults);
    const all = options ?? [];
    if (!q) return all.slice(0, maxResults);
    const nq = norm(q);
    return all.filter((o) => norm(o.label).includes(nq) || norm(o.value).includes(nq)).slice(0, maxResults);
  }, [typed, query, options, filter, maxResults]);

  const showError = !!invalid && touched;
  const inputText = open && typed ? query : labelForValue;

  const commit = (v: string) => {
    onChange(v);
    setQuery("");
    setTyped(false);
    setOpen(false);
    setTouched(true);
    setActive(-1);
  };

  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>
        {label}
        {required && <span className={s.req}> *</span>}
      </label>
      <div className={l.wrap}>
        <div className={s.inputWrap}>
          {leftIcon && <span className={s.inputIcon}>{leftIcon}</span>}
          <input
            id={name}
            name={name}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-invalid={showError || undefined}
            className={mergeClasses(s.input, l.chevPad, !!leftIcon && s.inputWithIcon, showError && s.inputInvalid)}
            value={inputText}
            placeholder={placeholder}
            onFocus={() => {
              setOpen(true);
              setTyped(false);
              setQuery("");
              setActive(-1);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setTyped(true);
              setOpen(true);
              setActive(0);
            }}
            onBlur={() => {
              blurTimer.current = setTimeout(() => {
                setOpen(false);
                setTouched(true);
                if (allowFreeText && typed && query.trim() && query.trim() !== labelForValue) {
                  onChange(query.trim());
                }
                setTyped(false);
                setQuery("");
              }, 130);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                // Von „keine Markierung" aus nach oben = ans Listenende.
                setActive((a) => (a < 0 ? results.length - 1 : Math.max(a - 1, 0)));
              } else if (e.key === "Enter") {
                // Nur eine WIRKLICH markierte Zeile wird übernommen (siehe
                // `active`): Ein Enter direkt nach dem Hineintabben darf keinen
                // Wert setzen, den niemand ausgewählt hat.
                if (open && active >= 0 && results[active]) {
                  e.preventDefault();
                  commit(results[active].value);
                }
              } else if (e.key === "Escape") {
                setOpen(false);
                setTyped(false);
                setQuery("");
              }
            }}
          />
          <SperrWert />
          <span className={l.chevron}><Chevron /></span>
        </div>
        {open && (
          <div className={l.list} role="listbox" onMouseDown={(e) => e.preventDefault()}>
            {results.length === 0 ? (
              <div className={l.empty}>{emptyText}</div>
            ) : (
              results.map((o, i) => (
                <div
                  key={`${o.value}-${i}`}
                  role="option"
                  aria-selected={o.value === value}
                  className={mergeClasses(l.opt, i === active && l.optActive)}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(o.value)}
                >
                  {o.icon}
                  <span>{o.label}</span>
                  {o.hint && <span className={l.optHint}>{o.hint}</span>}
                  {o.info && (
                    <button
                      type="button"
                      className={l.optInfoBtn}
                      title={o.info}
                      aria-label={`Erläuterung zu ${o.label}`}
                      aria-expanded={infoOffen === o.value}
                      // Klick klappt die Erläuterung auf, statt die Option zu
                      // wählen — sonst wäre der Text weg, bevor man ihn liest.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setInfoOffen((alt) => (alt === o.value ? null : o.value));
                      }}
                    >
                      ⓘ
                    </button>
                  )}
                  {o.info && infoOffen === o.value && (
                    <div className={l.optInfoText}>{o.info}</div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {showError && errorText && (
        <div className={s.errorMsg}>
          <ErrIcon />
          {errorText}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
